import { ChevronDown } from 'lucide-react';
import { forwardRef, SelectHTMLAttributes, useId } from 'react';

import cn from '@/utils/cn';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  // Either pass `options` or `<option>` children - both are supported.
  options?: SelectOption[];
  placeholder?: string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, helperText, options, placeholder, id, className, children, ...rest }, ref) => {
    const generatedId = useId();
    const selectId = id ?? generatedId;

    return (
      <div className="flex flex-col gap-1">
        {label && (
          <label htmlFor={selectId} className="text-sm font-medium text-ink">
            {label}
          </label>
        )}
        {/* appearance-none + a custom chevron so the arrow is always aligned and
            on-brand, instead of the misaligned OS-default select arrow.

            Do NOT add a hover/active background here. The browser paints a
            native <select>'s option list with the element's own computed
            `background-color`, and the pointer is still over the select while
            that list is open - so a `hover:bg-*` tints the whole open menu, not
            the closed box. Keep it `bg-white` and signal state with the border
            or the ring. */}
        <div className={cn('relative', className)}>
          <select
            ref={ref}
            id={selectId}
            className={cn(
              // 16px below `sm` so iOS Safari does not zoom the page on focus.
              'block h-11 w-full min-w-0 cursor-pointer appearance-none rounded-lg border bg-white py-2 pl-3 pr-9 text-base text-ink shadow-sm sm:h-9 sm:text-sm',
              'transition-colors focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand',
              'disabled:cursor-not-allowed disabled:opacity-60',
              error ? 'border-danger focus:ring-danger focus:border-danger' : 'border-line',
            )}
            aria-invalid={!!error}
            aria-describedby={error ? `${selectId}-error` : undefined}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...rest}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options
              ? options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))
              : children}
          </select>
          <ChevronDown
            aria-hidden
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40"
          />
        </div>
        {error && (
          <p id={`${selectId}-error`} className="text-xs text-danger" role="alert">
            {error}
          </p>
        )}
        {helperText && !error && <p className="text-xs text-ink/60">{helperText}</p>}
      </div>
    );
  },
);

Select.displayName = 'Select';

export default Select;
